// Capacitor offline bootstrap: install interceptors, network watcher, hydrate, auto-sync.
import { isCapacitorClient } from "modules/auth/refreshStorage";
import { installOfflineInterceptors, setOfflineAxiosStore } from "./axiosOffline";
import { startNetworkWatcher, isOnline, isConnected, onStatusChange, probeReachability } from "./network";
import { hydrateStoreFromCache } from "./hydrate";
import { runSync, retryFailedNow, setSyncStore, reconcileOrphanPendingFlags } from "./sync";
import { syncMobileLookups } from "./preloadLookups";
import { prefetchCurrentProject } from "./prefetch";
import { patchOfflineStatus } from "./status";
import { offlineStatusSnapshot, updateOutboxItem, removeConflict } from "./outbox";
import { ensureNotificationPermission, onNotificationAction } from "./notifications";
import { peekPendingConflict, presentConflict } from "./conflict";
import { applyKeepServerFromConflict } from "./resolvePending";

/*
Device smoke checklist:
1. Online: open QA list + a custom form record list (caches).
2. Airplane mode: reopen app — lists still show; navbar says Offline.
3. Create/edit a record offline with a photo — pending badge increments.
4. Go online — sync runs; success notification; pending clears.
5. Edit same record on web, then offline-edit on phone — clash notification + Keep phone/server.
6. Open a drawing PDF online once, then offline — PDF still opens.
7. Dead WiFi (connected, no Lion): navbar says Offline; save still works; retries when Lion answers.
8. Brief signal: pending items drain without waiting for app resume.
*/

const SYNC_TICK_MS = 30000;

const catchUp = (store, { forcePrefetch } = {}) => {
  runSync();
  syncMobileLookups(store);
  prefetchCurrentProject(store, { force: forcePrefetch });
};

export const refreshOfflineBadge = async (store) => {
  if (!store) return;
  store.dispatch(
    patchOfflineStatus({
      ...(await offlineStatusSnapshot()),
      online: isOnline(),
    })
  );
};

export const resolvePendingConflictsInteractively = async () => {
  const conflict = await peekPendingConflict();
  if (!conflict) return;

  const choice = await presentConflict({ label: conflict.label });
  if (choice === "server") {
    await applyKeepServerFromConflict(conflict);
  } else {
    await updateOutboxItem(conflict.outboxId, { status: "pending", force: true });
    await removeConflict(conflict.outboxId);
    await runSync();
  }
};

export const bootstrapOffline = async (store) => {
  const enabled = isCapacitorClient() || import.meta.env.VITE_OFFLINE_FORCE === "true";
  if (!enabled) return;

  setOfflineAxiosStore(store);
  setSyncStore(store);
  installOfflineInterceptors();

  store.dispatch(patchOfflineStatus({ online: isOnline() }));
  await hydrateStoreFromCache(store);
  await reconcileOrphanPendingFlags();

  onStatusChange((online) => {
    store.dispatch(patchOfflineStatus({ online }));
    if (online) catchUp(store, { forcePrefetch: true });
  });

  await startNetworkWatcher({
    onReconnect: () => catchUp(store, { forcePrefetch: true }),
    onResumeSync: () => catchUp(store),
  });

  await ensureNotificationPermission();
  await onNotificationAction(async (extra) => {
    if (extra?.type === "sync_clash") {
      await resolvePendingConflictsInteractively();
    }
  });

  if (isOnline()) {
    setTimeout(() => catchUp(store), 500);
  }

  // ponytail: 30s tick is enough for site signal that comes and goes; backoff lives on each item
  setInterval(async () => {
    if (!isConnected()) return;
    const ok = isOnline() ? true : await probeReachability();
    if (ok) catchUp(store);
  }, SYNC_TICK_MS);

  await refreshOfflineBadge(store);
};

export { runSync, retryFailedNow, isOnline };
